import { useCallback, useMemo, useState } from 'react';

import { SESSION_STORAGE_LEFT_MENU_DRAWER } from 'shared/constants';

import { useResponsive } from './useResponsive';
import { useSessionStorageState } from './useSessionStorageState';

/**
 * Custom hook to manage the state of the left menu drawer, with persisted state in session storage.
 *
 * @returns An object with the open state and convenient open/close/toggle methods.
 */
export function useLeftMenuDrawer() {
  const { isMobile } = useResponsive();

  const [stateShow, setStateShow] = useState(false);

  const [sessionStorageShow, setSessionStorageShow] = useSessionStorageState(
    SESSION_STORAGE_LEFT_MENU_DRAWER,
    isMobile ? false : true,
  );

  const show = useMemo(() => {
    return isMobile ? stateShow : sessionStorageShow;
  }, [isMobile, sessionStorageShow, stateShow]);

  const setShow = useCallback(
    (show: boolean | ((prev: boolean) => boolean)) => {
      if (isMobile) {
        // Only change the state show if it's mobile
        setStateShow(show);
      } else {
        // Only change the session storage show if it's not mobile
        setSessionStorageShow(show);
      }
    },
    [isMobile, setSessionStorageShow, setStateShow],
  );

  const open = useCallback(() => {
    setShow(true);
  }, [setShow]);

  const close = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const toggle = useCallback(() => {
    setShow(prev => !prev);
  }, [setShow]);

  return {
    isOpened: show,
    open,
    close,
    toggle,
  };
}
